import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading, Stat } from "./NKDaily";
import { CountryFlag, GlobeInline } from "@/lib/identity";
import { MagnifyingGlass, CheckCircle, Circle } from "@phosphor-icons/react";

const TOGGLES = [
  { key: "active_participation", label: "Active" },
  { key: "leaderboard_enabled", label: "Leaderboard" },
  { key: "country_titles_enabled", label: "Titles" },
  { key: "playoff_eligible", label: "Playoff" },
  { key: "prize_eligible", label: "Prize" },
];

export default function Countries() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [savingCode, setSavingCode] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/countries");
      setRows(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load countries"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (regionFilter !== "ALL") r = r.filter((c) => c.region === regionFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      r = r.filter((c) => c.country_name.toLowerCase().includes(needle) || c.country_code.toLowerCase().includes(needle));
    }
    return [...r].sort((a, b) => {
      if (a.playoff_eligible !== b.playoff_eligible) return a.playoff_eligible ? -1 : 1;
      if (a.is_african !== b.is_african) return a.is_african ? -1 : 1;
      return a.country_name.localeCompare(b.country_name);
    });
  }, [rows, q, regionFilter]);

  const totals = useMemo(() => ({
    total: rows.length,
    african: rows.filter((c) => c.is_african).length,
    playoff: rows.filter((c) => c.playoff_eligible).length,
    paused: rows.filter((c) => !c.active_participation).length,
  }), [rows]);

  const toggle = async (code, key, current) => {
    setSavingCode(code + key);
    try {
      await api.patch(`/admin/countries/${code}`, { [key]: !current });
      await load();
      toast.success(`${code} · ${key} → ${!current ? "ON" : "OFF"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingCode(null); }
  };

  return (
    <div className="space-y-6">
      <Heading
        title="Countries"
        subtitle="Manage country status, eligibility, and growth flags. Phase 1: Nigeria + Ghana playoff-active; all other African countries active for ranking + titles; Rest of World is a separate track."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Countries seeded" value={totals.total} />
        <Stat label="African" value={totals.african} />
        <Stat label="Playoff-active" value={totals.playoff} />
        <Stat label="Paused" value={totals.paused} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 border border-nk-line px-3 py-2 rounded-sm bg-nk-surface flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-nk-mute" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by country name or code…"
            data-testid="admin-countries-search"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-nk-dim"
          />
        </div>
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          data-testid="admin-countries-region"
          className="bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm"
        >
          <option value="ALL">All regions</option>
          <option value="West">West Africa</option>
          <option value="East">East Africa</option>
          <option value="Central">Central Africa</option>
          <option value="North">North Africa</option>
          <option value="Southern">Southern Africa</option>
          <option value="ROW">Rest of World</option>
        </select>
      </div>

      <div className="nk-card overflow-x-auto" data-testid="admin-countries-table">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="border-b border-nk-line">
            <tr>
              <th className="px-4 py-3 text-left text-xs text-nk-mute font-medium uppercase tracking-wider">Country</th>
              <th className="px-3 py-3 text-left text-xs text-nk-mute font-medium uppercase tracking-wider">Region</th>
              <th className="px-3 py-3 text-left text-xs text-nk-mute font-medium uppercase tracking-wider">Status</th>
              {TOGGLES.map((t) => (
                <th key={t.key} className="px-2 py-3 text-center text-xs text-nk-mute font-medium uppercase tracking-wider">{t.label}</th>
              ))}
              <th className="px-3 py-3 text-left text-xs text-nk-mute font-medium uppercase tracking-wider">Currency</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.country_code} className="border-b border-nk-line/40 last:border-0">
                <td className="px-4 py-2.5 flex items-center gap-2.5">
                  {c.country_code === "ROW" ? <GlobeInline size={16} /> : <CountryFlag country={c.country_code} size={18} />}
                  <span className="font-medium">{c.country_name}</span>
                  <span className="text-[10px] font-mono text-nk-dim">{c.country_code}</span>
                </td>
                <td className="px-3 py-2.5 text-nk-mute text-xs">{c.region}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                    c.launch_status === "playoff-ready"
                      ? "bg-nk-gold/15 text-nk-gold border border-nk-gold/30"
                      : c.launch_status === "paused"
                        ? "bg-nk-line text-nk-mute"
                        : "bg-nk-accent/15 text-nk-accent border border-nk-accent/30"
                  }`}>{c.launch_status}</span>
                </td>
                {TOGGLES.map((t) => {
                  const val = !!c[t.key];
                  const saving = savingCode === c.country_code + t.key;
                  return (
                    <td key={t.key} className="px-2 py-2.5 text-center">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => toggle(c.country_code, t.key, val)}
                        data-testid={`admin-country-${c.country_code.toLowerCase()}-${t.key}`}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-sm transition-colors ${
                          val
                            ? "text-nk-accent hover:bg-nk-accent/10"
                            : "text-nk-dim hover:bg-nk-surface"
                        } ${saving ? "opacity-50" : ""}`}
                        title={`${t.label}: ${val ? "ON" : "OFF"} — click to toggle`}
                      >
                        {val ? <CheckCircle size={20} weight="duotone" /> : <Circle size={20} />}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-xs font-mono text-nk-mute">{c.supported_currency || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-nk-dim">No countries match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-nk-dim">
        Tip: A country needs <span className="text-nk-text">Playoff</span> AND <span className="text-nk-text">Prize</span> ON to receive cash rewards at Day 30. Turning off <span className="text-nk-text">Active</span> hides the country from new signups.
      </p>
    </div>
  );
}
